/* eslint-disable no-return-assign */
import { ImpulseElement, property, registerElement, target, targets } from '@ambiki/impulse';
import type SubscriptionFormElement from 'subscriptions/elements/subscription_form_element';
import AddRemoveSeatElement from './add_remove_seat_element';

type SeatHash = {
  totalSeatsCount: number;
  costPerSeat: number;
};

@registerElement('manage-seat')
export default class ManageSeatElement extends ImpulseElement {
  /**
   * The interval to bill the customer.
   */
  @property() billingInterval: 'month' | 'year' = 'month';

  @target() subscriptionForm: SubscriptionFormElement;
  @target() totalCost: HTMLElement;
  @target() purchaseButton: HTMLButtonElement;
  @targets() addRemoveSeats: AddRemoveSeatElement[];

  @targets() monthPrices: HTMLElement[];
  @targets() yearPrices: HTMLElement[];
  @targets() billingIntervalRadio: HTMLInputElement[];

  private seatHash: Record<string, SeatHash> = {};

  /**
   * Called when `billingInterval` property changes.
   */
  billingIntervalChanged(interval: 'month' | 'year') {
    this.yearPrices.forEach((element) => (element.hidden = interval === 'month'));
    this.monthPrices.forEach((element) => (element.hidden = interval !== 'month'));
    this.addRemoveSeats.forEach((element) => (element.billingInterval = interval));
    this.subscriptionForm.billingInterval = interval;
  }

  /**
   * See `data-action="add-remove-seat:change->manage-seat#handleSeatChange"`
   */
  handleSeatChange(event: CustomEvent<{ name: string } & SeatHash>) {
    // Store the added/removed seat's attributes into a hash and reflect the changes to the UI.
    const { name, totalSeatsCount, costPerSeat } = event.detail;
    this.seatHash[name] = { totalSeatsCount, costPerSeat };
    this.reflectSeatChange();
    this.togglePurchaseButtonDisability();
  }

  updateBillingInterval(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.billingInterval = input.value as 'month' | 'year';
  }

  disableSeats() {
    this.disableAddRemoveSeats(true);
    this.disableBillingIntervalRadios(true);
  }

  enableSeats() {
    this.disableAddRemoveSeats(false);
    this.disableBillingIntervalRadios(false);
  }

  private reflectSeatChange() {
    this.totalCost.innerText = this.formatCurrency(this.totalSeatCost);
    this.subscriptionForm.subscriptionData = this.seatHash;
  }

  private togglePurchaseButtonDisability() {
    this.purchaseButton.disabled = this.totalSeatCost === 0;
  }

  private get totalSeatCost() {
    return Object.values(this.seatHash).reduce((sum, hash) => sum + hash.costPerSeat * hash.totalSeatsCount, 0);
  }

  private formatCurrency(cost: number) {
    return Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cost);
  }

  private disableAddRemoveSeats(value: boolean) {
    for (const element of this.addRemoveSeats) {
      element.disabled = value;
    }
  }

  private disableBillingIntervalRadios(value: boolean) {
    for (const element of this.billingIntervalRadio) {
      element.disabled = value;
    }
  }
}
